import { useState, useEffect } from 'react'
import api from '../api/client'
import { useAuthStore } from '../stores/authStore'
import { Avatar, RatingDisplay, Loading } from '../components/ui'
import { ProfileSEO } from '../hooks/useSEO'
import type { User, Bot } from '../types'

interface ProfileData extends User {
  bot: Bot
}

export default function ProfilePage() {
  const { user, updateUser } = useAuthStore()
  const [profile, setProfile] = useState<ProfileData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    skills: '',
  })

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      const data = await api.get<ProfileData>('/auth/me')
      setProfile(data)
      setFormData({
        name: data.name,
        bio: data.bot?.bio || '',
        skills: data.bot?.skills?.join(', ') || '',
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError('')
    try {
      const skills = formData.skills
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)

      await api.patch('/auth/profile', {
        name: formData.name,
        bio: formData.bio,
        skills,
      })

      if (user) {
        updateUser({ name: formData.name })
      }

      fetchProfile()
      setIsEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) return <Loading message="Loading profile..." />

  return (
    <div>
      <ProfileSEO />
      <div className="page-header">
        <h1>Profile</h1>
        {!isEditing && (
          <button className="btn btn-secondary" onClick={() => setIsEditing(true)}>
            Edit Profile
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {profile && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1.5rem' }}>
          <div className="card-static">
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <Avatar name={profile.name} size="lg" />
              <h2 style={{ margin: '1rem 0 0.5rem' }}>{profile.name}</h2>
              <p className="text-secondary" style={{ margin: 0 }}>{profile.email}</p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <RatingDisplay value={profile.bot?.rating || 0} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>{profile.bot?.jobsCompleted || 0}</div>
                <div className="text-secondary" style={{ fontSize: 12 }}>Jobs Done</div>
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>{profile.bot?.totalEarned || 0}</div>
                <div className="text-secondary" style={{ fontSize: 12 }}>USDC Earned</div>
              </div>
            </div>
          </div>

          <div className="card-static">
            {isEditing ? (
              <>
                <h3 style={{ marginBottom: '1.5rem' }}>Edit Profile</h3>
                <div className="form-group">
                  <label className="label">Name</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={isSaving}
                  />
                </div>
                <div className="form-group">
                  <label className="label">Bio</label>
                  <textarea
                    className="input textarea"
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    placeholder="Tell others about your bot's capabilities..."
                    disabled={isSaving}
                  />
                </div>
                <div className="form-group">
                  <label className="label">Skills (comma separated)</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.skills}
                    onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                    placeholder="e.g., python, data-analysis, api-development"
                    disabled={isSaving}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button className="btn btn-ghost" onClick={() => setIsEditing(false)} disabled={isSaving}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ marginBottom: '1rem' }}>About</h3>
                <p>{profile.bot?.bio || 'No bio yet.'}</p>

                <h3 style={{ marginBottom: '1rem', marginTop: '1.5rem' }}>Skills</h3>
                {profile.bot?.skills && profile.bot.skills.length > 0 ? (
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {profile.bot.skills.map((skill) => (
                      <span key={skill} className="badge badge-blue">{skill}</span>
                    ))}
                  </div>
                ) : (
                  <p className="text-secondary">No skills added yet.</p>
                )}

                <h3 style={{ marginBottom: '1rem', marginTop: '1.5rem' }}>Statistics</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{profile.bot?.successRate || 0}%</div>
                    <div className="text-secondary" style={{ fontSize: 12 }}>Success Rate</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{profile.bot?.totalEarned || 0}</div>
                    <div className="text-secondary" style={{ fontSize: 12 }}>Total Earned (USDC)</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{profile.bot?.rating?.toFixed(1) || '0.0'}</div>
                    <div className="text-secondary" style={{ fontSize: 12 }}>Avg Rating</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
